import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDateLocale } from "../../hooks/useDateLocale";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import dayjs from "dayjs";

import { AuthContext } from "../../contexts/AuthContext";
import { CONFIG } from "../../services/config";

import { FormInputText } from "../../components/form/FormInputText";
import MyModal from "../../components/MyModal";

type Props = {
  campaign: any;
  onClose: Function;
  openSucessAlert?: Function;
  openErrorAlert?: Function;
  refreshPage?: Function;
};

enum ParticipantType {
  EMAIL = "EMAIL",
  BATCH = "BATCH",
  VEHICLE = "VEHICLE",
}

interface FormValues {
  campaignName: string;
  type: ParticipantType;
  participant: string;
  batchFile: File | null;
  batchFileName: string;
  model: string;
  version: string;
  vehicleYear: string;
  startDate: string;
  endDate: string;
}

const getToday = () => {
  const currentDate = new Date();

  return `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
};

const formatDateInput = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";

const getBatchFileName = (campaign: any) =>
  campaign?.fileName ||
  campaign?.filename ||
  campaign?.batchFileName ||
  campaign?.spreadsheetName ||
  campaign?.originalFileName ||
  campaign?.rules?.[0]?.fileName ||
  "-";

const getCampaignId = (campaign: any) =>
  campaign?.id || campaign?._id || campaign?.campaignId;

interface BatchJob {
  jobId: string;
  status: "pending" | "processing" | "done" | "failed";
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_BATCH_EXTENSIONS = ["xls", "xlsx"];

export default function ModalEditCampaign({
  campaign,
  onClose,
  openSucessAlert,
  openErrorAlert,
  refreshPage,
}: Props) {
  const { t } = useTranslation();
  const { dateFormat, adapterLocale } = useDateLocale();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);
  const batchInputRef = useRef<HTMLInputElement>(null);
  const batchPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [batchJob, setBatchJob] = useState<BatchJob>({
    jobId: "",
    status: "pending",
  });
  const [showInactiveConfirmation, setShowInactiveConfirmation] =
    useState(false);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [loadingCampaignDetails, setLoadingCampaignDetails] = useState(false);
  const [campaignDetails, setCampaignDetails] = useState<any>(null);

  const campaignData = campaignDetails || campaign;
  const campaignId = getCampaignId(campaign);
  const participantType = useMemo(() => {
    if (campaignData?.batchJobId) {
      return ParticipantType.BATCH;
    }

    const campaignType = campaignData?.campaignType || campaignData?.type;
    if (
      campaignType &&
      Object.values(ParticipantType).includes(campaignType)
    ) {
      return campaignType;
    }

    if (
      campaignData?.modelId ||
      campaignData?.versionId ||
      campaignData?.vehicleYear
    ) {
      return ParticipantType.VEHICLE;
    }

    if (Array.isArray(campaignData?.rules) && campaignData.rules[0]?.email) {
      return ParticipantType.EMAIL;
    }

    return ParticipantType.BATCH;
  }, [campaignData]);

  const participantValue =
    campaignData?.rules?.[0]?.email ||
    campaignData?.email ||
    campaignData?.participantCount?.toString() ||
    "";

  const today = getToday();

  const formData = yup.object().shape({
    campaignName: yup
      .string()
      .required(t("ottoTrialPeriod.edit.validation.campaignNameRequired"))
      .test(
        "no-only-spaces",
        t("ottoTrialPeriod.edit.validation.campaignNameRequired"),
        (value) => !value || value.trim().length > 0,
      ),
    startDate: yup
      .string()
      .required(t("ottoTrialPeriod.edit.validation.startDateRequired")),
    batchFile: yup
      .mixed<File>()
      .nullable()
      .test(
        "batch-file-extension",
        t("ottoTrialPeriod.add.validation.batchFileInvalidExtension"),
        (file) => {
          if (!file) {
            return true;
          }

          const extension = file.name.split(".").pop()?.toLowerCase();
          return !!extension && ALLOWED_BATCH_EXTENSIONS.includes(extension);
        },
      )
      .test(
        "batch-file-size",
        t("ottoTrialPeriod.add.validation.batchFileMaxSize"),
        (file) => !file || file.size <= MAX_FILE_SIZE,
      ),
    endDate: yup
      .string()
      .required(t("ottoTrialPeriod.edit.validation.endDateRequired"))
      .test(
        "is-valid-range",
        t("ottoTrialPeriod.edit.validation.endDateGreaterThanStartDate"),
        (value, context) => {
          const { startDate } = context.parent as FormValues;
          if (!startDate || !value) {
            return true;
          }

          return value > startDate;
        },
      ),
  });

  const { register, handleSubmit, formState, reset, setValue, watch, control } =
    useForm<FormValues>({
    resolver: yupResolver(formData) as unknown as Resolver<FormValues>,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      campaignName: campaignData?.name || campaignData?.campaignName || "",
      type: participantType,
      participant: participantValue,
      batchFile: null,
      batchFileName: getBatchFileName(campaignData),
      model:
        campaignData?.model || campaignData?.modelName || campaignData?.modelId || "",
      version:
        campaignData?.version ||
        campaignData?.versionName ||
        campaignData?.versionId ||
        "",
      vehicleYear: campaignData?.vehicleYear?.toString() || "",
      startDate: formatDateInput(campaignData?.startDate),
      endDate: formatDateInput(campaignData?.endDate),
    },
    });

  const { errors } = formState;
  const selectedBatchFile = watch("batchFile");
  const isVehicleCampaign = participantType === ParticipantType.VEHICLE;
  const isBatchCampaign = participantType === ParticipantType.BATCH;
  const isEmailCampaign = participantType === ParticipantType.EMAIL;

  const requestConfig = {
    headers: {
      ...tokenHeaders.headers,
      countrycode: countryCode || tokenHeaders.headers.countryCode,
      countryCode: countryCode || tokenHeaders.headers.countryCode,
      "Content-Type": "application/json",
    },
  };

  useEffect(() => {
    if (!campaignId) {
      return;
    }

    setLoadingCampaignDetails(true);
    axios
      .get(`${CONFIG.API_URL}temporaryAccess/${campaignId}`, requestConfig)
      .then(({ data }) => {
        setCampaignDetails(data);
      })
      .catch(() => {
        openErrorAlert?.();
      })
      .finally(() => {
        setLoadingCampaignDetails(false);
      });
  }, [campaignId]);

  useEffect(() => {
    reset({
      campaignName: campaignData?.name || campaignData?.campaignName || "",
      type: participantType,
      participant: participantValue,
      batchFile: null,
      batchFileName: getBatchFileName(campaignData),
      model:
        campaignData?.model ||
        campaignData?.modelName ||
        campaignData?.modelId ||
        "",
      version:
        campaignData?.version ||
        campaignData?.versionName ||
        campaignData?.versionId ||
        "",
      vehicleYear: campaignData?.vehicleYear?.toString() || "",
      startDate: formatDateInput(campaignData?.startDate),
      endDate: formatDateInput(campaignData?.endDate),
    });
  }, [campaignData, participantType, participantValue, reset]);

  const verifyBatchStatus = async (jobId: string) => {
    const response = await axios.get(
      `${CONFIG.API_URL}temporaryAccess/batch/${jobId}`,
      tokenHeaders,
    );
    return response.data;
  };

  const startBatchStatusLoopCheck = async (jobId: string) => {
    const result = await verifyBatchStatus(jobId);

    if (result.status === "PENDING" || result.status === "PROCESSING") {
      batchPollingTimeoutRef.current = setTimeout(() => {
        setBatchJob({ jobId, status: "processing" });
      }, 3000);

      return;
    }

    if (result.status === "DONE") {
      setBatchJob({ jobId, status: "done" });
      return;
    }

    setBatchJob({ jobId, status: "failed" });
  };

  useEffect(() => {
    if (batchJob.status === "processing") {
      startBatchStatusLoopCheck(batchJob.jobId).catch(() => {
        setBatchJob({ jobId: batchJob.jobId, status: "failed" });
      });

      return;
    }

    if (batchJob.status === "done") {
      refreshPage?.();
      onClose();
      openSucessAlert?.(t("ottoTrialPeriod.alert.editSuccessMessage"));
      return;
    }

    if (batchJob.status === "failed") {
      onClose();
      openErrorAlert?.();
    }
  }, [batchJob]);

  useEffect(() => {
    return () => {
      if (batchPollingTimeoutRef.current) {
        clearTimeout(batchPollingTimeoutRef.current);
      }
    };
  }, []);

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const handleBatchFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setValue("batchFile", file, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const openBatchFileChooser = () => {
    batchInputRef.current?.click();
  };

  const editCampaignService: SubmitHandler<FormValues> = async (values) => {
    const originalCampaignName =
      campaignData?.name || campaignData?.campaignName || "";
    const originalStartDate = formatDateInput(campaignData?.startDate);
    const originalEndDate = formatDateInput(campaignData?.endDate);
    const payload: Record<string, string> = {};

    if (values.campaignName !== originalCampaignName) {
      payload.campaignName = values.campaignName;
    }

    if (values.startDate !== originalStartDate) {
      payload.startDate = `${values.startDate}T00:00:00`;
    }

    if (values.endDate !== originalEndDate) {
      payload.endDate = `${values.endDate}T23:59:59`;
    }

    try {
      if (Object.keys(payload).length) {
        await axios.put(
          `${CONFIG.API_URL}temporaryAccess/${campaignId}`,
          payload,
          requestConfig,
        );
      }

      if (isBatchCampaign && values.batchFile) {
        const formDataBatch = new FormData();
        formDataBatch.append("file", values.batchFile);
        formDataBatch.append("campaignId", String(campaignId));
        formDataBatch.append(
          "metadata",
          JSON.stringify({
            campaignId,
            campaignName: values.campaignName,
            countryCode: countryCode || tokenHeaders.headers.countryCode,
            startDate: `${values.startDate}T00:00:00`,
            endDate: `${values.endDate}T23:59:59`,
          }),
        );

        const batchResponse = await axios.post(
          `${CONFIG.API_URL}temporaryAccess/batch`,
          formDataBatch,
          {
            ...tokenHeaders,
            headers: {
              ...tokenHeaders.headers,
              countrycode: countryCode || tokenHeaders.headers.countryCode,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        const { jobId } = batchResponse.data;
        setBatchJob({ jobId, status: "processing" });
        return;
      }

      refreshPage?.();
      onClose();
      openSucessAlert?.(t("ottoTrialPeriod.alert.editSuccessMessage"));
    } catch (error: any) {
      onClose();
      openErrorAlert?.();
    }
  };

  const inactiveCampaignService = async () => {
    try {
      setLoadingInactive(true);
      await axios.delete(
        `${CONFIG.API_URL}temporaryAccess/${campaignId}`,
        requestConfig,
      );

      refreshPage?.();
      setShowInactiveConfirmation(false);
      onClose();
      openSucessAlert?.(t("ottoTrialPeriod.alert.inactiveSuccessMessage"));
    } catch (error: any) {
      setShowInactiveConfirmation(false);
      onClose();
      openErrorAlert?.();
    } finally {
      setLoadingInactive(false);
    }
  };

  const renderParticipantFields = () => {
    if (isVehicleCampaign) {
      return (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.edit.vehicle")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    disabled
                    id="editModel"
                    type="text"
                    label={t("ottoTrialPeriod.edit.model")}
                    {...register("model")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    disabled
                    id="editVersion"
                    type="text"
                    label={t("ottoTrialPeriod.edit.version")}
                    {...register("version")}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInputText
                    disabled
                    id="editVehicleYear"
                    type="text"
                    label={t("ottoTrialPeriod.edit.year")}
                    {...register("vehicleYear")}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      );
    }

    if (isBatchCampaign) {
      return (
        <>
          <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box>
                <span>{t("ottoTrialPeriod.edit.batchParticipants")}</span>
                <Box sx={{ fontSize: 12, lineHeight: 1.35, marginTop: 0.5 }}>
                  <div>{t("ottoTrialPeriod.edit.batchFileType")}</div>
                  <div>{t("ottoTrialPeriod.edit.batchFileLimit")}</div>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <input
                ref={batchInputRef}
                type="file"
                accept=".xls,.xlsx"
                style={{ display: "none" }}
                onChange={handleBatchFileChange}
              />
              <Box
                sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  gap: "2rem",
                  minWidth: 0,
                }}
              >
                <Button
                  variant="contained"
                  type="button"
                  onClick={openBatchFileChooser}
                  sx={{
                    backgroundColor: "#dfe4e8",
                    boxShadow: "none",
                    color: "#8f9aa3",
                    flexShrink: 0,
                    minWidth: 170,
                    "&:hover": {
                      backgroundColor: "#d7dde2",
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("ottoTrialPeriod.edit.chooseFileButton")}
                </Button>
                <Box
                  sx={{
                    color: "#8f9aa3",
                    fontSize: 14,
                    lineHeight: "36px",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedBatchFile?.name || getBatchFileName(campaignData)}
                </Box>
              </Box>
              {!!errors.batchFile && (
                <Box
                  sx={{
                    color: "var(--red-100)",
                    fontSize: 12,
                    marginTop: 1,
                  }}
                >
                  {errors.batchFile.message}
                </Box>
              )}
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      );
    }

    if (isEmailCampaign) {
      return (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.edit.participants")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <FormInputText
                disabled
                id="editParticipant"
                type="text"
                label={t("ottoTrialPeriod.edit.email")}
                {...register("participant")}
              />
            </Grid>
          </Grid>

          {HorizontalLine}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <MyModal
        title={t("ottoTrialPeriod.edit.title")}
        modalChange={onClose}
        component="form"
        onSubmit={handleSubmit(editCampaignService)}
        width="80%"
        maxWidth="72rem"
      >
        {loadingCampaignDetails && (
          <Box sx={{ display: "flex", justifyContent: "center", marginY: 3 }}>
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <span>{t("ottoTrialPeriod.edit.identification")}</span>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <FormInputText
              id="editCampaignName"
              type="text"
              label={t("ottoTrialPeriod.edit.campaignName")}
              slotProps={{ htmlInput: { maxLength: 50, minLength: 1 } }}
              error={errors.campaignName}
              {...register("campaignName")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormInputText
              select
              disabled
              id="editType"
              label={t("ottoTrialPeriod.edit.participantType")}
              value={participantType}
              {...register("type")}
            >
              {Object.values(ParticipantType).map((type) => (
                <MenuItem key={type} value={type}>
                  {t(
                    `ottoTrialPeriod.add.participantTypeOptions.${type.toLowerCase()}`,
                  )}
                </MenuItem>
              ))}
            </FormInputText>
          </Grid>
        </Grid>

        {HorizontalLine}

        {renderParticipantFields()}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <span>{t("ottoTrialPeriod.edit.period")}</span>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t("ottoTrialPeriod.edit.startDate")}
                    format={dateFormat}
                    minDate={dayjs(today)}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(val) => field.onChange(val ? dayjs(val).format("YYYY-MM-DD") : "")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t("ottoTrialPeriod.edit.endDate")}
                    format={dateFormat}
                    minDate={dayjs(today)}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(val) => field.onChange(val ? dayjs(val).format("YYYY-MM-DD") : "")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!errors.endDate,
                        helperText: errors.endDate?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {HorizontalLine}

        <p style={{ fontSize: 12, marginBottom: 30 }}>
          {t("ottoTrialPeriod.edit.requiredFields")}
        </p>

        <Box sx={{ display: "flex", gap: "1rem", marginY: "1rem" }}>
          <Button
            variant="contained"
            type="submit"
            disabled={!formState.isValid || !campaignId}
          >
            {t("ottoTrialPeriod.edit.saveButton")}
          </Button>
          <Button
            variant="contained"
            color="error"
            type="button"
            disabled={
              !campaignId ||
              campaignData?.status === "INACTIVE" ||
              campaignData?.status === "CANCELLED"
            }
            onClick={() => setShowInactiveConfirmation(true)}
          >
            {t("ottoTrialPeriod.edit.inactiveButton")}
          </Button>
        </Box>
      </MyModal>

      <Dialog
        open={showInactiveConfirmation}
        onClose={() => setShowInactiveConfirmation(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              maxWidth: "36rem",
              padding: "2rem 4rem",
              textAlign: "center",
              width: "100%",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 28,
            fontWeight: 700,
            padding: 0,
            position: "relative",
          }}
        >
          {t("ottoTrialPeriod.edit.inactiveTitle")}
          <Button
            onClick={() => setShowInactiveConfirmation(false)}
            sx={{
              color: "#001e50",
              fontSize: 28,
              minWidth: "auto",
              padding: 0,
              position: "absolute",
              right: -40,
              top: -8,
            }}
          >
            x
          </Button>
        </DialogTitle>
        <DialogContent sx={{ padding: "2rem 0 1.5rem" }}>
          <Box sx={{ fontSize: 16, lineHeight: 1.5 }}>
            <div>{t("ottoTrialPeriod.edit.inactiveDescription")}</div>
            <div>{t("ottoTrialPeriod.edit.inactiveWarning")}</div>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            "& > :not(:first-of-type)": {
              marginLeft: 0,
            },
            alignItems: "center",
            flexDirection: "column",
            gap: "1rem",
            padding: 0,
          }}
        >
          <Button
            variant="contained"
            onClick={inactiveCampaignService}
            disabled={loadingInactive}
            sx={{
              borderRadius: "24px",
              maxWidth: "20rem",
              width: "100%",
            }}
          >
            {loadingInactive ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              t("ottoTrialPeriod.edit.inactiveButton")
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowInactiveConfirmation(false)}
            disabled={loadingInactive}
            sx={{
              borderRadius: "24px",
              maxWidth: "20rem",
              width: "100%",
              color: "#00AFF2",
            }}
          >
            {t("ottoTrialPeriod.edit.cancelButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}